import React, { useState } from 'react';
import { Slider, Tooltip } from 'antd';
import {
	PauseCircleOutlined,
	PlayCircleOutlined,
	InfoCircleOutlined,
	CloseOutlined,
	DownloadOutlined,
} from '@ant-design/icons';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import { useAudioPlayer } from 'src/contexts/AudioPlayerContext';

const formatTime = (seconds: number) => {
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDuration = (seconds: number) => {
	if (seconds < 60) {
		return `${seconds?.toFixed(0)} seconds`;
	}
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins} min ${secs?.toFixed(0)} sec`;
};

const AudioPlayerToolbar: React.FC = () => {
	const {
		isPlaying,
		currentAudioUrl,
		currentTime,
		duration,
		currentRecord,
		playAudio,
		pauseAudio,
		stopAudio,
		seekTo,
		audioSourceInfo,
		type,
		volume,
		setVolume,
		downloadAudio,
		playbackSpeed,
		setPlaybackSpeed,
	} = useAudioPlayer();

	const [isMuted, setIsMuted] = useState(false);
	const [previousVolume, setPreviousVolume] = useState(1);

	const handleMuteToggle = () => {
		if (isMuted) {
			setVolume(previousVolume);
			setIsMuted(false);
		} else {
			setPreviousVolume(volume);
			setVolume(0);
			setIsMuted(true);
		}
	};

	if (!currentAudioUrl || !currentRecord) {
		return null;
	}

	const tooltipContent = (
		<div className="text-left min-w-max">
			<div>
				<strong> {audioSourceInfo}</strong>
			</div>
			<div className="text-nowrap">
				<strong>Call SID:</strong> {currentRecord?.callSid || 'N/A'}
			</div>
			<div className="text-nowrap">
				<strong>Date:</strong> {currentRecord?.date || 'N/A'}
			</div>
			<div className="text-nowrap">
				<strong>Duration:</strong> {formatDuration(Number(duration))}
			</div>
			<div className="text-nowrap">
				<strong>Forwarded From:</strong> {currentRecord?.forwardedFrom || 'N/A'}
			</div>
		</div>
	);

	return (
		<div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white shadow-lg rounded-xl border border-gray-200 z-[2000]">
			<div className="px-6 py-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-1">
						<div className="flex items-center space-x-1">
							<Tooltip title={isPlaying ? 'Pause' : 'Play'}>
								<button
									onClick={() =>
										isPlaying ? pauseAudio() : playAudio(currentAudioUrl, currentRecord, type)
									}
									className="p-2 rounded-full hover:bg-gray-100 transition-colors"
								>
									{isPlaying ? (
										<PauseCircleOutlined className="text-xl text-blue-600" />
									) : (
										<PlayCircleOutlined className="text-xl text-blue-600" />
									)}
								</button>
							</Tooltip>
							<Tooltip title="Stop">
								<button
									onClick={stopAudio}
									className="p-2 rounded-full hover:bg-gray-100 transition-colors"
								>
									<StopIcon className="text-2xl text-red-600" />
								</button>
							</Tooltip>
							<Tooltip title="Download">
								<button
									onClick={downloadAudio}
									className="p-2 rounded-full hover:bg-gray-100 transition-colors"
								>
									<DownloadOutlined className="text-xl text-blue-600" />
								</button>
							</Tooltip>
						</div>

						<div className="flex-1 min-w-[200px]">
							<div className="flex items-center space-x-2">
								<span className="text-sm text-gray-600 w-auto pl-1 pr-2">
									{formatTime(currentTime)}
								</span>
								<Slider
									value={currentTime}
									max={duration}
									onChange={seekTo}
									tooltip={{ formatter: (value) => formatTime(value as number) }}
									className="flex-1"
								/>
								<span className="text-sm text-gray-600 w-auto pl-2 pr-1">
									{formatTime(duration)}
								</span>
							</div>
						</div>

						<div className="flex items-center space-x-2">
							<div className="relative group">
								<button
									onClick={handleMuteToggle}
									className="p-2 rounded-full hover:bg-gray-100 transition-colors relative"
								>
									{isMuted || volume === 0 ? (
										<div className="relative">
											<VolumeOffIcon className="text-xl text-gray-600" />
											<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-0.5 bg-gray-600 rotate-45" />
										</div>
									) : volume > 0 ? (
										<VolumeUpIcon className="text-xl text-blue-600" />
									) : (
										<VolumeOffIcon className="text-xl text-blue-600" />
									)}
								</button>
								<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
									<div className="h-20 flex items-center py-1">
										<Slider
											vertical
											value={isMuted ? 0 : volume}
											min={0}
											max={1}
											step={0.1}
											onChange={(value) => {
												setVolume(value);
												if (value > 0) {
													setIsMuted(false);
												}
											}}
											tooltip={{ formatter: (value) => `${Math.round((value as number) * 100)}%` }}
										/>
									</div>
								</div>
							</div>

							{/* Playback Speed Control */}
							<div className="relative pr-1">
								<div className="w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex items-center justify-center group">
									<span className="font-bold text-gray-700" style={{ fontSize: '12px' }}>
										{playbackSpeed}x
									</span>

									<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
										<div className="bg-white rounded-lg shadow-lg border border-gray-200 py-1">
											{[1, 1.5, 2, 2.5].map((speed) => (
												<button
													key={speed}
													onClick={() => setPlaybackSpeed(speed)}
													className={`block w-full px-3 py-1 text-left hover:bg-gray-50 transition-colors ${
														speed === playbackSpeed ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
													}`}
													style={{ fontSize: '12px' }}
												>
													{speed}x
												</button>
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<Tooltip title="Close">
						<button
							onClick={stopAudio}
							className="p-2 rounded-full hover:bg-gray-100 transition-colors"
						>
							<CloseOutlined className="text-gray-500" />
						</button>
					</Tooltip>
					<Tooltip
						title={tooltipContent}
						className="ml-1"
						overlayStyle={{ maxWidth: '350px', minWidth: '350px' }}
					>
						<div className="flex items-center space-x-1">
							<InfoCircleOutlined
								style={{ fontSize: '16px' }}
								className="text-gray-400 cursor-help"
							/>
						</div>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

export default AudioPlayerToolbar;
